'use client'

import { useState } from 'react'
import { FileDropzone, FileDropzoneProps } from '.'
import { Upload } from 'flowbite-react-icons/outline'
import { FileRejection } from 'react-dropzone'

export interface UploadedFile {
  name: string
  key?: string
}

const uploadFile = async (file: File, uploadPath?: string) => {
  try {
    // console.log('🚀 Starting upload process for:', {
    //   fileName: file.name,
    //   fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    //   fileType: file.type,
    //   uploadPath: uploadPath || 'root'
    // })

    const response = await fetch('/api/s3/upload_presigned_url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contentType: file.type, uploadPath: uploadPath }),
    })

    if (!response.ok) {
      // console.error('❌ Presigned URL request failed:', response.status, response.statusText)
      throw new Error(`Presigned URL failed: ${response.status}`)
    }

    const { key, url, fields } = await response.json()
    // console.log('✅ Got presigned URL:', {
    //   key,
    //   uploadUrl: url,
    //   fieldsCount: Object.keys(fields).length
    // })

    const formData = new FormData()
    Object.entries(fields).forEach(([fieldKey, value]) => {
      formData.append(fieldKey, value as string)
    })
    formData.append('file', file)

    // console.log('📤 Uploading file to Digital Ocean Spaces...')
    // console.log('Upload URL:', url)
    // console.log('File key:', key)

    const uploadResponse = await fetch(url, {
      method: 'POST',
      body: formData,
    })

    // console.log('📥 Digital Ocean response:', {
    //   status: uploadResponse.status,
    //   statusText: uploadResponse.statusText,
    //   ok: uploadResponse.ok,
    //   headers: Object.fromEntries(uploadResponse.headers.entries())
    // })

    if (uploadResponse.ok || uploadResponse.status === 204) {
      // if (uploadResponse.status === 204) {
      //   console.log('✅ Upload successful! Status 204 No Content (expected for Digital Ocean)')
      //   
      //   // Special handling for PDF files
      //   if (file.type === 'application/pdf') {
      //     console.log('📄 PDF file uploaded successfully:', {
      //       fileName: file.name,
      //       key: key,
      //       size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
      //     })
      //   }
      // } else {
      //   console.log('✅ Upload successful! Status:', uploadResponse.status)
      // }
      // 
      // console.log('🎯 Final upload result:', {
      //   fileName: file.name,
      //   fileKey: key,
      //   uploadComplete: true
      // })
      
      return key as string
    } else {
      // console.error('❌ Upload failed with status:', uploadResponse.status)
      // const errorText = await uploadResponse.text().catch(() => 'No error text available')
      // console.error('Error response body:', errorText)
      throw new Error(`Upload failed: ${uploadResponse.status}`)
    }
  } catch (error) {
    // console.error('💥 Upload error:', error)
    // console.error('File details:', {
    //   name: file.name,
    //   size: file.size,
    //   type: file.type
    // })
    return undefined
  }
}

export interface DirectUploadDropzoneProps
  extends Omit<FileDropzoneProps, 'loading' | 'onChange'> {
  onChange: (
    uploadedFile: UploadedFile[],
    fileRejections: FileRejection[]
  ) => void
  uploadPath?: string
}

export const DirectUploadDropzone = ({
  children,
  accept,
  onChange,
  uploadPath,
  maxSize = 10 * 1024 * 1024,
  multiple = false,
  color = 'grayborder',
  disabled = false,
}: DirectUploadDropzoneProps) => {
  const [loading, setLoading] = useState(false)
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])

  return (
    <>
      <FileDropzone
        onChange={async (files, fileRejections) => {
          setFileRejections(fileRejections)

          setLoading(true)
          const uploadedFiles: UploadedFile[] = []
          
          for (const file of files) {
            const key = await uploadFile(file, uploadPath)
            if (key) {
              uploadedFiles.push({ name: file.name, key: key })
            }
          }
          
          setLoading(false)
          onChange(uploadedFiles, fileRejections)
        }}
        accept={accept}
        multiple={multiple}
        maxSize={maxSize}
        disabled={disabled}
        color={color}
        loading={loading}
      >
        <Upload className="mb-2 h-6 w-6 text-gray-400" />
        {children}
      </FileDropzone>
      {fileRejections.length > 0 && (
        <span className="mt-2 text-red-600 text-sm">
          {fileRejections[0].errors
            .map((error) => error.message)
            .join('&nbsp;')}
        </span>
      )}
    </>
  )
}
